import React, { useCallback } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';

import { useAutoSuggest } from '@uncinc/react-searchkit';

import { SEARCHKIT_AUTOCOMPLETE_RENDER_SUGGESTIONS_THRESHOLD } from '../../../../config/constants';
import SearchAutocomplete from '../../../Search/Autocomplete';
import SearchAutocompleteResult from '../../../Search/AutocompleteResult';
import SearchAutosuggestResult from '../../../Search/AutosuggestResult';
import type { SearchBarProps } from '../../../Search/Bar';
import type { SearchAllContentHit } from '../../types';

export interface SearchAllContentAutocompleteInnerProps {
  type?: SearchBarProps['type'];
  searchBarVisible?: boolean;
  setSearchBarVisible?: (searchBarVisible: boolean) => void;
  clearOnSearch?: boolean;
  onSearchSubmit?: (searchQuery: string) => void;
  onSuggestionSelected?: (suggestion: SearchAllContentHit) => void;
}

const renderResult = (hit: SearchAllContentHit) => {
  if (hit.autocomplete_text) {
    return <SearchAutocompleteResult hit={hit} />;
  }

  return <SearchAutosuggestResult hit={hit} />;
};

// eslint-disable-next-line max-len
const SearchAllContentAutocompleteInner: React.FC<SearchAllContentAutocompleteInnerProps> = (props) => {
  const {
    type = 'primary',
    searchBarVisible = false,
    setSearchBarVisible,
    clearOnSearch = false,
    onSearchSubmit,
    onSuggestionSelected,
  } = props;

  const { clearSearch } = useAutoSuggest();
  const { t } = useTranslation();

  const classNames = c('SearchAllContent__SearchBar');
  const activeElement = document.activeElement as HTMLInputElement | undefined;

  // Clearing the search is something we have to handle internally,
  // within this searchkit context. Otherwise we won't have access
  // to the useAutoSuggest hook clearSearch function.
  const handleClear = useCallback(() => {
    activeElement?.blur();

    if (clearOnSearch) {
      clearSearch();
    }

    if (setSearchBarVisible) {
      setSearchBarVisible(false);
    }
  }, [activeElement, clearOnSearch, clearSearch, setSearchBarVisible]);

  const handleSearchSubmit = useCallback((query: string) => {
    handleClear();

    if (onSearchSubmit) {
      onSearchSubmit(query);
    }
  }, [handleClear, onSearchSubmit]);

  const handleSuggestionSelected = useCallback((suggestion: SearchAllContentHit) => {
    handleClear();

    if (onSuggestionSelected) {
      onSuggestionSelected(suggestion);
    }
  }, [handleClear, onSuggestionSelected]);

  return (
    <SearchAutocomplete
      getSuggestionValue={(hit: SearchAllContentHit) => (
        hit.autocomplete_text?.[0] || hit.title?.[0]
      )}
      renderSuggestion={renderResult}
      shouldRenderSuggestions={(value) => (
        value.trim().length > SEARCHKIT_AUTOCOMPLETE_RENDER_SUGGESTIONS_THRESHOLD
      )}
      onSuggestionsClearRequested={() => ''}
      onSuggestionSelected={(e, { suggestion }: { suggestion: SearchAllContentHit }) => {
        handleSuggestionSelected(suggestion);
      }}
      searchbarProps={{
        type,
        autoFocus: searchBarVisible,
        onSubmit: handleSearchSubmit,
        className: classNames,
        placeholder: t('components.search_all_content.search_bar.placeholder'),
      }}
    />
  );
};

export default SearchAllContentAutocompleteInner;
